import AgencySaleDialogView from 'features/Agency/components/AgencySaleDialogView';
import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import AppColors from 'general/constants/AppColors';
import AppResource from 'general/constants/AppResource';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import DialogAddVehicleView from '../DialogAddVehicleView';
import Utils from 'general/utils/Utils';
import AppConstants from 'general/constants/AppConstants';
import energySwapIcon from 'assets/icons/TMT/energy-swap.svg';
import thunderIcon from 'assets/icons/TMT/thunder.svg';
import thunderBrickIcon from 'assets/icons/TMT/thunder-brick.svg';

CardVehicleView.propTypes = {
  vehicle: PropTypes.object,
  onRefresh: PropTypes.func,
};

function CardVehicleView({ vehicle, onRefresh }) {
  // MARK: ---- PROPERTIES ----
  const { user, permissionList } = useSelector((state) => state.auth);
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
  const color =
    vehicle?.vehicleType === ChargeConstants.VehicleType.CAR
      ? AppColors.primary
      : 'rgba(50, 173, 230, 1)';
  const isPlugAndChargeEnabled =
    vehicle?.chargingMode === ChargeConstants.VehicleChargingMode.SMART || vehicle?.autoCharge === true;
  const vehicleIdentityStatus = vehicle?.vehicleIdentity?.status;
  const vehicleIdentityStatusName =
    vehicleIdentityStatus === 'ACTIVE'
      ? 'Được phép sạc'
      : vehicleIdentityStatus === 'INACTIVE'
        ? 'Bị chặn sạc'
        : '';
  const vehicleIdentityStatusStyle =
    vehicleIdentityStatus === 'ACTIVE'
      ? {
        backgroundColor: '#E6FFDB',
        color: '#1BA05C',
      }
      : vehicleIdentityStatus === 'INACTIVE'
        ? {
          backgroundColor: AppColors.dangerLight,
          color: AppColors.danger,
        }
        : null;

  // MARK: ---- METHODS ----
  const buildMoreActions = (row) => {
    const actions = [];
    if (isAdmin) {
      actions.push({
        text: 'Chỉnh sửa',
        iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          handleEdit(row);
        },
      });
      actions.push({
        text: isPlugAndChargeEnabled
          ? 'Tắt sạc nhận diện xe (Plug & Charge)'
          : 'Bật sạc nhận diện xe (Plug & Charge)',
        iconElement: <img
          src={isPlugAndChargeEnabled ? thunderBrickIcon : thunderIcon}
          alt=""
          style={{ width: '1.1rem', height: '1.1rem' }}
        />,
        click: () => {
          handleTogglePlugAndCharge(row);
        },
      });
      actions.push({
        text: 'Xóa',
        iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
        click: () => {
          handleDelete(row);
        },
      });
    }
    return actions;
  };
  const handleEdit = (row) => {
    AppCustomDialogHelper.show(
      'Sửa xe',
      <DialogAddVehicleView
        item={row}
        onCancel={() => AppCustomDialogHelper.hide()}
        onDone={() => {
          AppCustomDialogHelper.hide();
          if (onRefresh) onRefresh();
        }}
      />,
      'lg',
      false,
      true
    );
  };
  const handleDelete = (row) => {
    ChargeHelper.UI.Vehicle.showDialogConfirmDeleteVehicle(row, () => {
      if (onRefresh) onRefresh();
    });
  };
  const handleTogglePlugAndCharge = (row) => {
    AppCustomDialogHelper.show(
      '',
      <AgencySaleDialogView
        iconEl={
          <img
            src={
              isPlugAndChargeEnabled
                ? AppResource.images.dialogIcons.dialogError
                : AppResource.images.dialogIcons.dialogSuccess
            }
          />
        }
        title={isPlugAndChargeEnabled ? 'Tắt sạc nhận diện xe' : 'Bật sạc nhận diện xe'}
        contentEl={
          <>
            Bạn có chắc muốn {isPlugAndChargeEnabled ? 'tắt' : 'bật'} tính năng sạc nhận diện xe
            (Plug &amp; Charge) cho xe{' '}
            <span style={UIHelper.buildFontStyle('700', '14px', AppColors.gray600)}>
              [{row?.plate}]
            </span>{' '}
            không?
          </>
        }
        cancelButtonText="Bỏ qua"
        confirmButtonText={isPlugAndChargeEnabled ? 'Tắt' : 'Xác nhận'}
        confirmButtonRed={isPlugAndChargeEnabled}
        onCancel={() => {
          AppCustomDialogHelper.hide();
        }}
        onConfirm={async () => {
          AppCustomDialogHelper.hide();
          const result = await ChargeHelper.API.Vehicle.requestUpdateAutoChargeConfig(
            row?.vehicleId,
            !isPlugAndChargeEnabled
          );
          if (result === true) {
            ToastHelper.showSuccess(
              isPlugAndChargeEnabled
                ? 'Đã tắt sạc nhận diện xe (Plug & Charge)'
                : 'Đã bật sạc nhận diện xe (Plug & Charge)'
            );
            if (onRefresh) onRefresh();
            return;
          }

          ToastHelper.showError(result || 'Có lỗi xảy ra');
        }}
      />,
      'md',
      true,
      true
    );
  };

  return (
    <div
      className="overflow-hidden position-relative"
      style={{
        border: `1px solid ${AppColors.gray[300]}`,
        borderRadius: '10px',
        backgroundColor: color,
      }}
    >
      <div
        style={{
          backgroundColor: '#F9F9F9',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          marginBottom: '5px',
        }}
      >
        {/* icon */}
        <div
          className="position-absolute rounded-circle d-flex align-items-center justify-content-center"
          style={{
            top: '-20px',
            right: '-20px',
            opacity: 0.1,
          }}
        >
          <i
            className={`fa-regular ${vehicle?.vehicleType === ChargeConstants.VehicleType.CAR
              ? 'fa-car-side-bolt'
              : 'fa-motorcycle'
              }`}
            style={{
              color: 'rgba(64, 70, 116, 1)',
              fontSize: '10em',
            }}
          />
        </div>
        {/* header */}
        <div
          className="px-4 pt-6 pb-4 d-flex justify-content-between"
          style={{
            borderBottom: `1px dashed ${AppColors.gray[300]}`,
          }}
        >
          <div className="d-flex flex-column align-items-start">
            <div className="d-flex align-items-center gap-2">
              <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                {vehicle?.model?.name}
              </span>
              {isPlugAndChargeEnabled && (
                <span
                  className="d-flex align-items-center gap-1 px-2 py-1"
                  style={{
                    backgroundColor: '#E6FFDB',
                    borderRadius: '4px',
                    padding: '3px 6px',
                    gap: '5px',
                    color: '#1BA05C',
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: '20px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <img src={energySwapIcon} alt="" style={{ width: '18px', height: '18px' }} />
                  Tự động sạc
                </span>
              )}
            </div>
            <span style={UIHelper.buildFontStyle('700', '38px', color)}>{vehicle?.plate}</span>
          </div>
          {Utils.checkPermission(permissionList, AppConstants.permissionCode.driverUpdate.code) &&
            UIHelper.buildMoreActionsButton(buildMoreActions(vehicle))}
        </div>
        {/* info */}
        <div className="p-4 d-flex flex-column gap-2">
          <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
            Số khung:{' '}
            <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
              {vehicle?.vinNumber}
            </span>
          </span>
          {vehicle?.vehicleType === ChargeConstants.VehicleType.CAR && (
            <div className="d-flex align-items-center justify-content-between gap-2">
              <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
                MAC/EVCC:{' '}
                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                  {vehicle?.vehicleIdentity?.code ?? vehicle?.evccId}
                </span>
              </span>
              {vehicleIdentityStatusStyle && (
                <span
                  className="px-2 py-1"
                  style={{
                    ...UIHelper.buildFontStyle('600', '12px', vehicleIdentityStatusStyle.color),
                    backgroundColor: vehicleIdentityStatusStyle.backgroundColor,
                    borderRadius: '4px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {vehicleIdentityStatusName}
                </span>
              )}
            </div>
          )}
          <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
            Đầu Socket:{' '}
            <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
              {vehicle?.model?.connectorType}
            </span>
          </span>
          <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
            Số ID Tag:{' '}
            <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
              {_.first(vehicle?.cards)?.idTag}
            </span>
          </span>
          <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[700])}>
            Dung tích pin:{' '}
            <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
              {vehicle?.model?.batteryCapacity} kWh
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default CardVehicleView;
